import type { Placement, Strategy } from "@floating-ui/react-dom";
import classNames from "classnames";
import { noop } from "lodash";
import * as React from "react";
import { Component, RefObject, useEffect } from "react";
import * as ReactDOM from "react-dom";
import { Keys } from "~/common/enums";
import { usePopup } from "~/sharedComponents/inlinePopup/usePopup";
import { useClickOutside } from "~/sharedHOC/ClickOutside/useClickOutside";
import { domUtils } from "~/utils/domUtils";
import { preventJqueryEsc } from "../../common/utils/preventJqueryEsc";
import { FocusTrap, FocusTrapProps } from "../../sharedHOC/adjustContainer/FocusTrap";
import { CloseCallback, CloseReason } from "../../sharedHOC/ClickOutside/ClickOutside";
import { isBlockedByChildPopup, useBlockPointerEvents } from "../hooks/useBlockPointerEvents";
import * as styles from "./InlinePopup.less";
import { PopupArrow } from "./PopupArrow";

export type InlinePopupProps = Pick<
    FocusTrapProps,
    "handleFocusedElementUnmounting" | "enforceFocus" | "autoFocus" | "className"
> &
    Pick<Parameters<typeof useClickOutside>[0], "disableClosingIfChildPopupOpened"> & {
        isOpen: boolean;

        /**
         *  See https://floating-ui.com/docs/computePosition#placement
         *  @default "bottom-start"
         */
        placement?: Placement;
        /**
         *  See https://floating-ui.com/docs/flip#fallbackplacements
         */
        fallbackPlacements?: Placement[];
        /**
         *  This is the type of CSS position property to use.
         *  See https://floating-ui.com/docs/computePosition#strategy
         *
         *  The fixed one should be used for nested popup when the target of
         *  current popup has parent element with scroll/hidden overflow.
         *  With fixed position we prevent parent popup closing when user clicks somewhere
         *  in nested popup because in this case nested popup is descendant element of parent popup.
         *
         *  @default "absolute"
         */
        strategy?: Strategy;
        showOverTrigger?: boolean;
        /**
         * If true, there will be no distance between popup and trigger elements.
         * Designed for EntitiesGrid.
         */
        showWithoutIndent?: boolean;
        /**
         * NOTE: if rendered to body, popup will be hidden if the target (trigger) element is not visible on the screen.
         * That might happen when user scrolls the container of target element. This is useful because
         *   - prevents window scrollbars appearance if target element somebody bellow the viewport.
         *   - prevents popup to rendered outside of target's scrollable container when this container is small.
         */
        renderToBody?: boolean;
        /**
         * Indicates that popup should be rendered with arrow centered to the trigger element.
         */
        renderArrow?: boolean;
        /**
         * Clicks outside don't work, just closes popup (i.e. if click some button
         * outside of popup then button's click action won't be invoked).
         * @default true.
         */
        blockClicksOutside?: boolean;

        triggerElRef: RefObject<HTMLElement>;

        closeActionHandler?: CloseCallback;

        /**
         * Prevents popup closing when ESC key is pressed but
         * this event was prevented by some other component inside popup.
         */
        ignorePreventedEsc?: boolean;

        /**
         * For preventing task/project details closing when esc is pressed inside popup
         */
        preventJqueryEsc?: boolean;
        width?: number | string;
        minWidth?: number;
        /**
         * If true, popup width will match trigger width.
         * If minWidth is also set, maximum between minWidth and trigger width will be used.
         * Useful for popups in grid with resizable columns.
         */
        matchTriggerWidth?: boolean;
        /**
         * If true, popup min-width will be set to width of trigger element.
         */
        enableDropDownBehavior?: boolean;
        /**
         * Indicates that the popup shouldn't be closed after esc/clicks outside.
         */
        disableClosing?: boolean | ((e: React.SyntheticEvent | UIEvent) => boolean);
    };

export class InlinePopup extends Component<InlinePopupProps> {
    static defaultProps: Partial<InlinePopupProps> = {
        autoFocus: false,
        enforceFocus: false,
        closeActionHandler: noop,
    };

    render() {
        return this.props.isOpen && <InlinePopupWrapper {...this.props}>{this.props.children}</InlinePopupWrapper>;
    }
}

const InlinePopupWrapper: React.FC<InlinePopupProps> = props => {
    const { popupStyle, refs, placement, arrowY, arrowX, arrowRef } = usePopup(props);

    useBlockPointerEvents({
        activeRefs: [props.triggerElRef, refs.floating],
        disabled: !props.blockClicksOutside,
    });

    const { keyUpHandler } = useKeyupHandler(refs.floating, props);
    usePreventJqueryEsc(refs.floating, props);

    if (props.enableDropDownBehavior && !props.width) {
        popupStyle.minWidth = props.triggerElRef.current?.offsetWidth ?? 0;
    }

    if (props.matchTriggerWidth && !props.width) {
        popupStyle.width = Math.max(props.triggerElRef.current?.offsetWidth ?? 0, props.minWidth ?? 0);
    }

    if (props.minWidth && !props.width) {
        popupStyle.minWidth = Math.max(props.minWidth, (popupStyle.minWidth as number) ?? 0);
    }

    useClickOutside({
        containers: [refs.floating, props.triggerElRef],
        closeActionHandler: props.closeActionHandler,
        disabled: typeof props.disableClosing === "function" ? false : props.disableClosing,
        disableClosingIfChildPopupOpened: props.disableClosingIfChildPopupOpened,
        disableClosing: typeof props.disableClosing === "function" ? props.disableClosing : null,
    });

    let content = (
        <>
            <FocusTrap
                containerRef={refs.floating}
                autoFocus={props.autoFocus}
                enforceFocus={props.enforceFocus}
                className={classNames(styles.popup, props.className)}
                style={popupStyle}
                onKeyUp={keyUpHandler}
                handleFocusedElementUnmounting={props.handleFocusedElementUnmounting}
            >
                {/*
                This data-test-id used in userflow tours, if you change it, please update folowing tours:
                    - 1. Resource Planning (paid, interactive)
            */}
                <div data-testid="popover" className={styles.inner_container}>
                    {props.children}
                    {props.renderArrow && <PopupArrow ref={arrowRef} x={arrowX} y={arrowY} placement={placement} />}
                </div>
            </FocusTrap>
            {/*
                to catch keyUp for "tab" event if this popup is the last element in DOM (renderToBody == true).
                Otherwise the adress bar gets the focus, and there will be no "keyup" to handle by InlinePopup -> useKeyupHandler
            */}
            {props.renderToBody && !props.enforceFocus && <div tabIndex={0} />}
        </>
    );

    return props.renderToBody ? ReactDOM.createPortal(content, document.body) : content;
};

InlinePopupWrapper.defaultProps = {
    blockClicksOutside: true,
};

const usePreventJqueryEsc = (popupRef: RefObject<HTMLElement>, props: InlinePopupProps) => {
    useEffect(() => {
        if (!props.preventJqueryEsc || !popupRef.current) {
            return;
        }
        const $el = $(popupRef.current);
        $el.on("keydown", preventJqueryEsc);
        return () => $el.off("keydown", preventJqueryEsc);
    }, [popupRef.current]);
};

const useKeyupHandler = (popupRef: RefObject<HTMLElement>, props: InlinePopupProps) => {
    const keyUpHandler = (event: React.KeyboardEvent | KeyboardEvent) => {
        if (
            !props.closeActionHandler ||
            (typeof props.disableClosing === "function" ? props.disableClosing(event) : props.disableClosing)
        ) {
            return;
        }

        switch (event.key) {
            case Keys.Escape:
                const isContentBlockedByChildPopup =
                    props.disableClosingIfChildPopupOpened && isBlockedByChildPopup(popupRef.current);

                // ignore document event if ignorePreventedEsc is set because
                // in this case we call preventDefault() on react synthetic events to prevent popup
                // closing.
                const shouldIgnorePreventedEsc = props.ignorePreventedEsc && event.defaultPrevented;

                if (!shouldIgnorePreventedEsc && !isContentBlockedByChildPopup) {
                    props.closeActionHandler(CloseReason.ESC, event);
                }
                break;
            case Keys.Tab:
                const isDocumentEvent = !(event as React.KeyboardEvent).nativeEvent;
                const isActiveElementOutside = domUtils.isOutside(popupRef.current, document.activeElement);

                if (isDocumentEvent && isActiveElementOutside) {
                    props.closeActionHandler(CloseReason.TAB, event);
                }
                break;
        }
    };

    // to close popup when user press esc somewhere outside of popup (case when focused element was removed from popup, after this browser moves focus to body).
    const keyupHandlerRef = React.useRef(keyUpHandler);
    keyupHandlerRef.current = keyUpHandler;
    useEffect(() => {
        const callback: typeof keyUpHandler = e => keyupHandlerRef.current(e);
        document.addEventListener("keyup", callback);
        return () => document.removeEventListener("keyup", callback);
    }, []);

    return { keyUpHandler } as const;
};
